import React from "react";
import { motion } from "framer-motion";

/**
 * Vansha Stage Illustrations — one distinct SVG per lifecycle stage.
 * No people. Concept-based iconography using the Marigold & Aubergine palette.
 */

const V = "#E8552B";       // vermilion
const M = "#F4A72C";       // marigold
const S = "#7BA098";       // sage
const A = "#1B1533";       // aubergine
const CREAM = "#FBF4E8";
const BLUSH = "#FBE4D6";

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: { pathLength: 1, opacity: 1 },
};

/* Stage 1 — Planning / Pre-conception: calendar + seed cycle */
export function StagePlanning({ size = 220, className = "" }) {
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} className={className} data-testid="stage-illus-planning">
      <circle cx="120" cy="120" r="90" fill={BLUSH} />
      {/* cycle ring */}
      <circle cx="120" cy="120" r="70" fill="none" stroke={S} strokeWidth="4" strokeDasharray="4 6" opacity="0.7" />
      {/* fertility arc highlight */}
      <motion.path d="M 120 50 A 70 70 0 0 1 190 120"
        fill="none" stroke={V} strokeWidth="8" strokeLinecap="round"
        initial={draw.hidden} whileInView={draw.visible} viewport={{ once: true }} transition={{ duration: 1.5 }} />
      {/* center seed */}
      <ellipse cx="120" cy="120" rx="22" ry="30" fill={M} />
      <ellipse cx="120" cy="120" rx="10" ry="18" fill={V} />
      {/* small dots for days */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        return <circle key={deg} cx={120 + 70 * Math.cos(rad)} cy={120 + 70 * Math.sin(rad)} r="3.5" fill={A} />;
      })}
    </svg>
  );
}

/* Stage 2 — Pregnancy: bump silhouette with trimester rings */
export function StagePregnancy({ size = 220, className = "" }) {
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} className={className} data-testid="stage-illus-pregnancy">
      <circle cx="120" cy="120" r="90" fill={BLUSH} />
      {/* orbit rings for trimesters */}
      {[85, 65, 45].map((r, i) => (
        <motion.circle key={r} cx="120" cy="130" r={r} fill="none"
          stroke={[V, M, S][i]} strokeWidth="3" opacity="0.8"
          initial={draw.hidden} whileInView={draw.visible} viewport={{ once: true }}
          transition={{ duration: 1.3, delay: i * 0.2 }}
        />
      ))}
      {/* seed baby */}
      <ellipse cx="120" cy="140" rx="20" ry="26" fill={A} />
      <circle cx="120" cy="120" r="6" fill={M} />
      {/* radiating dots */}
      <circle cx="200" cy="60" r="4" fill={V} />
      <circle cx="55" cy="70" r="3" fill={M} />
      <circle cx="180" cy="200" r="3" fill={S} />
    </svg>
  );
}

/* Stage 3 — Delivery: heartbeat pulse line */
export function StageDelivery({ size = 220, className = "" }) {
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} className={className} data-testid="stage-illus-delivery">
      <circle cx="120" cy="120" r="90" fill={BLUSH} />
      {/* heart */}
      <motion.path
        d="M 120 90 C 100 70, 65 75, 65 110 C 65 145, 105 165, 120 180 C 135 165, 175 145, 175 110 C 175 75, 140 70, 120 90 Z"
        fill={V}
        initial={{ scale: 0, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        style={{ transformOrigin: "120px 120px" }}
      />
      {/* pulse line */}
      <motion.path
        d="M 40 130 L 70 130 L 82 100 L 96 160 L 110 130 L 200 130"
        fill="none" stroke={A} strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round"
        initial={draw.hidden} whileInView={draw.visible} viewport={{ once: true }}
        transition={{ duration: 1.4, delay: 0.4 }}
      />
      <circle cx="120" cy="120" r="10" fill={M} />
    </svg>
  );
}

/* Stage 4 — Newborn: crescent moon over bassinet */
export function StageNewborn({ size = 220, className = "" }) {
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} className={className} data-testid="stage-illus-newborn">
      <circle cx="120" cy="120" r="90" fill={BLUSH} />
      {/* moon */}
      <circle cx="130" cy="90" r="34" fill={M} />
      <circle cx="140" cy="82" r="30" fill={BLUSH} />
      {/* stars */}
      <path d="M 70 60 l 3 6 l 6 3 l -6 3 l -3 6 l -3 -6 l -6 -3 l 6 -3 z" fill={V} />
      <path d="M 190 130 l 2 4 l 4 2 l -4 2 l -2 4 l -2 -4 l -4 -2 l 4 -2 z" fill={V} />
      <circle cx="80" cy="110" r="3" fill={A} />
      {/* bassinet base */}
      <path d="M 55 165 Q 120 145 185 165 L 175 190 Q 120 175 65 190 Z" fill={S} />
      <path d="M 55 165 Q 120 145 185 165" fill="none" stroke={A} strokeWidth="2" opacity="0.4" />
      {/* pillow */}
      <ellipse cx="120" cy="170" rx="24" ry="8" fill={CREAM} />
    </svg>
  );
}

/* Stage 5 — Infant: milk bottle + rattle */
export function StageInfant({ size = 220, className = "" }) {
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} className={className} data-testid="stage-illus-infant">
      <circle cx="120" cy="120" r="90" fill={BLUSH} />
      {/* bottle body */}
      <rect x="95" y="90" width="50" height="90" rx="10" fill={CREAM} stroke={A} strokeWidth="2" />
      {/* milk level */}
      <path d="M 97 130 Q 120 138 143 130 L 143 178 Q 120 178 97 178 Z" fill={M} opacity="0.85" />
      {/* nipple */}
      <path d="M 105 90 Q 120 65 135 90 Z" fill={V} />
      <rect x="100" y="82" width="40" height="10" rx="3" fill={A} />
      {/* rattle */}
      <circle cx="180" cy="170" r="14" fill={V} />
      <rect x="176" y="180" width="8" height="24" rx="3" fill={A} />
      <circle cx="180" cy="170" r="4" fill={CREAM} />
      {/* sparkle */}
      <path d="M 60 70 l 2 6 l 6 2 l -6 2 l -2 6 l -2 -6 l -6 -2 l 6 -2 z" fill={S} />
    </svg>
  );
}

/* Stage 6 — Toddler: footprints */
export function StageToddler({ size = 220, className = "" }) {
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} className={className} data-testid="stage-illus-toddler">
      <circle cx="120" cy="120" r="90" fill={BLUSH} />
      {/* footprint 1 - left */}
      <g transform="translate(80 130) rotate(-15)">
        <ellipse cx="0" cy="0" rx="18" ry="26" fill={V} />
        <circle cx="-10" cy="-30" r="5" fill={V} />
        <circle cx="-2" cy="-34" r="4" fill={V} />
        <circle cx="6" cy="-32" r="4" fill={V} />
        <circle cx="12" cy="-28" r="3" fill={V} />
      </g>
      {/* footprint 2 - right */}
      <g transform="translate(155 90) rotate(15)">
        <ellipse cx="0" cy="0" rx="18" ry="26" fill={M} />
        <circle cx="10" cy="-30" r="5" fill={M} />
        <circle cx="2" cy="-34" r="4" fill={M} />
        <circle cx="-6" cy="-32" r="4" fill={M} />
        <circle cx="-12" cy="-28" r="3" fill={M} />
      </g>
      {/* connector dashed */}
      <path d="M 80 130 Q 120 100 155 90" fill="none" stroke={A} strokeWidth="2" strokeDasharray="4 5" opacity="0.5" />
    </svg>
  );
}

/* Stage 7 — Early Childhood: sprout growing into leaves */
export function StageChildhood({ size = 220, className = "" }) {
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} className={className} data-testid="stage-illus-childhood">
      <circle cx="120" cy="120" r="90" fill={BLUSH} />
      {/* pot */}
      <path d="M 88 170 L 92 200 L 148 200 L 152 170 Z" fill={V} />
      <rect x="85" y="164" width="70" height="10" rx="2" fill={A} />
      {/* stem */}
      <motion.path d="M 120 170 L 120 100" fill="none" stroke={S} strokeWidth="4" strokeLinecap="round"
        initial={draw.hidden} whileInView={draw.visible} viewport={{ once: true }} transition={{ duration: 1.2 }} />
      {/* left leaf */}
      <motion.ellipse cx="98" cy="130" rx="22" ry="10" fill={S} transform="rotate(-30 98 130)"
        initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.6, duration: 0.5 }}
        style={{ transformOrigin: "98px 130px" }}
      />
      {/* right leaf */}
      <motion.ellipse cx="142" cy="120" rx="22" ry="10" fill={S} transform="rotate(30 142 120)"
        initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.8, duration: 0.5 }}
        style={{ transformOrigin: "142px 120px" }}
      />
      {/* flower */}
      <motion.g initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.1 }}
        style={{ transformOrigin: "120px 90px" }}>
        <circle cx="120" cy="80" r="8" fill={M} />
        <circle cx="108" cy="90" r="8" fill={M} />
        <circle cx="132" cy="90" r="8" fill={M} />
        <circle cx="114" cy="70" r="8" fill={M} />
        <circle cx="126" cy="70" r="8" fill={M} />
        <circle cx="120" cy="82" r="5" fill={V} />
      </motion.g>
    </svg>
  );
}

export const STAGES = [
  { key: "planning",   name: "Planning",        sub: "Trying to conceive",  Illus: StagePlanning  },
  { key: "pregnancy",  name: "Pregnancy",       sub: "Forty weeks of care", Illus: StagePregnancy },
  { key: "delivery",   name: "Delivery",        sub: "The first breath",    Illus: StageDelivery  },
  { key: "newborn",    name: "Newborn",         sub: "First 100 days",      Illus: StageNewborn   },
  { key: "infant",     name: "Infant",          sub: "0 – 12 months",       Illus: StageInfant    },
  { key: "toddler",    name: "Toddler",         sub: "1 – 3 years",         Illus: StageToddler   },
  { key: "childhood",  name: "Early Childhood", sub: "3 – 6 years",         Illus: StageChildhood },
];
